import { useRef, useState, useEffect } from "react";
import * as THREE from "three";

import useSceneManager from "./hooks/use_scene_manager";
import useScreenDimension from "./hooks/use_screen_dimensions";
import useARElements from "./hooks/useARElements";
import useWebXRAR from "./hooks/useWebXRAR";
import { useAROverlay } from "./hooks/useAROverlay";

export default function App() {
  const refBody = useRef();
  const container = refBody;
  const videoRef = useRef(null);

  const [inAR, setInAR] = useState(false);
  const [arMessage, setARMessage] = useState("");

  const { inH, inW } = useScreenDimension(container);
  const { renderer, scene, camera } = useSceneManager(inH, inW);

  const { cube, planeMesh } = useARElements(scene);
  const { verticalOffset, setVerticalOffset, BackButtonComponent, SliderComponent } = useAROverlay();

  // Initialize inline AR
  useWebXRAR(
    renderer,
    scene,
    camera,
    inAR,
    cube,
    planeMesh,
    setARMessage,
    verticalOffset,
    videoRef
  );

  // Enable AR with rear camera
  const arHandler = async () => {
    setInAR(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: "environment" } },
      });
      videoRef.current.srcObject = stream;
    } catch (err) {
      console.error("Camera error:", err);
      setARMessage("❌ Cannot access rear camera");
    }
  };

  // Append Three.js renderer
  useEffect(() => {
    if (renderer && container.current) {
      container.current.appendChild(renderer.domElement);
      renderer.domElement.style.position = "absolute";
      renderer.domElement.style.top = 0;
      renderer.domElement.style.left = 0;
      renderer.domElement.style.pointerEvents = "none";
    }
  }, [renderer]);

  return (
    <div style={{ position: "relative", width: "100vw", height: "100vh" }}>
      {/* 1️⃣ Video feed */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
        }}
      />

      {/* 2️⃣ Three.js canvas */}
      <div
        ref={refBody}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
        }}
      ></div>

      {/* 3️⃣ Enable AR button */}
      {!inAR && (
        <button
          onClick={arHandler}
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            zIndex: 10,
            padding: "10px 20px",
            borderRadius: "6px",
            background: "rgba(0,0,0,0.6)",
            color: "white",
            border: "none",
          }}
        >
          Enable AR
        </button>
      )}

      {/* 4️⃣ AR message */}
      {arMessage && (
        <div
          style={{
            position: "absolute",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.7)",
            color: "white",
            padding: "10px 20px",
            borderRadius: "8px",
            zIndex: 20,
          }}
        >
          {arMessage}
        </div>
      )}

      {/* 5️⃣ Overlay UI */}
      {inAR && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 30,
            pointerEvents: "none",
          }}
        >
          <div style={{ pointerEvents: "auto" }}>
            <BackButtonComponent onClick={() => setInAR(false)} />
            <SliderComponent value={verticalOffset} onChange={setVerticalOffset} />
          </div>
        </div>
      )}
    </div>
  );
}
